import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

function kernelDensityEstimator(kernel, X) {
  return V => X.map(x => [x, d3.mean(V, v => kernel(x - v))]);
}
function kernelEpanechnikov(k) {
  return v => {
    v = v / k;
    return Math.abs(v) <= 1 ? (0.75 * (1 - v * v)) / k : 0;
  };
}

// Renders violins comparing previous and selected weeks or a single custom range
function WeekComparisonViolinChart({ current = [], previous = [], customLabel }) {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!wrapperRef.current || !svgRef.current) return;

    const container = d3.select(wrapperRef.current);
    const svg = d3.select(svgRef.current);
    const { width, height } = container.node().getBoundingClientRect();
    svg.attr('width', width).attr('height', height);

    const weeks = previous.length
      ? [
          {
            label: 'Previous week',
            // Ignore readings below 30 kPa
            valsA: previous
              .map(r => +r['200-PT-2002'])
              .filter(v => !isNaN(v) && v >= 30),
            valsB: previous
              .map(r => +r['300-PT-3001'])
              .filter(v => !isNaN(v) && v >= 30),
          },
          {
            label: customLabel || 'Selected week',
            // Ignore readings below 30 kPa
            valsA: current
              .map(r => +r['200-PT-2002'])
              .filter(v => !isNaN(v) && v >= 30),
            valsB: current
              .map(r => +r['300-PT-3001'])
              .filter(v => !isNaN(v) && v >= 30),
          },
        ]
      : [
          {
            label: customLabel || 'Selected week',
            // Ignore readings below 30 kPa
            valsA: current
              .map(r => +r['200-PT-2002'])
              .filter(v => !isNaN(v) && v >= 30),
            valsB: current
              .map(r => +r['300-PT-3001'])
              .filter(v => !isNaN(v) && v >= 30),
          },
        ];

    const allValues = weeks.flatMap(w => [...w.valsA, ...w.valsB]);
    if (!allValues.length) {
      svg.selectAll('*').remove();
      return;
    }

    const sortedValues = allValues.slice().sort(d3.ascending);
    const lower = d3.quantile(sortedValues, 0.01);
    const upper = d3.quantile(sortedValues, 0.99);

    weeks.forEach(w => {
      w.valsA = w.valsA.filter(v => v >= lower && v <= upper);
      w.valsB = w.valsB.filter(v => v >= lower && v <= upper);
    });

    const margin = { top: 20, right: 30, bottom: 30, left: 40 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const y = d3.scaleLinear()
      .domain([lower, upper])
      .nice()
      .range([innerHeight, 0]);

    const x = d3.scaleBand()
      .domain(weeks.map(w => w.label))
      .range([0, innerWidth])
      .padding(0.4);

    const ticks = y.ticks(40);
    const kde = kernelDensityEstimator(kernelEpanechnikov(7), ticks);

    let maxDensity = 0;
    const densityData = weeks.map(w => {
      const left = w.valsA.length ? kde(w.valsA) : ticks.map(t => [t, 0]);
      const right = w.valsB.length ? kde(w.valsB) : ticks.map(t => [t, 0]);
      maxDensity = Math.max(maxDensity, d3.max(left, d => d[1]) || 0, d3.max(right, d => d[1]) || 0);
      return { label: w.label, left, right };
    });

    const widthScale = d3.scaleLinear()
      .domain([0, maxDensity])
      .range([0, x.bandwidth() / 2]);

    const areaLeft = d3.area()
      .curve(d3.curveBasis)
      .x0(0)
      .x1(d => -widthScale(d[1]))
      .y(d => y(d[0]));

    const areaRight = d3.area()
      .curve(d3.curveBasis)
      .x0(0)
      .x1(d => widthScale(d[1]))
      .y(d => y(d[0]));

    svg.selectAll('*').remove();
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .style('font-size', '10px');

    g.append('g')
      .call(d3.axisLeft(y))
      .selectAll('text')
      .style('font-size', '10px');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -margin.left + 12)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff')
      .style('font-size', '10px')
      .text('Feed Pressure (kPa)');

    const groups = g.selectAll('.week')
      .data(densityData)
      .join('g')
      .attr('transform', d => `translate(${x(d.label) + x.bandwidth() / 2},0)`);

    groups.append('path')
      .attr('d', d => areaLeft(d.left))
      .attr('fill', '#22c55e')
      .attr('opacity', 0.7);

    groups.append('path')
      .attr('d', d => areaRight(d.right))
      .attr('fill', '#3b82f6')
      .attr('opacity', 0.7);
  }, [current, previous, customLabel]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div ref={wrapperRef} style={{ width: '100%', height: 'calc(100% - 20px)' }}>
        <svg ref={svgRef} />
      </div>
      <div className="flex justify-center space-x-4 text-xs mt-1">
        <div className="flex items-center space-x-1">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: '#22c55e' }}></span>
          <span>DMS Cyclone</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className="w-3 h-3 rounded-full inline-block" style={{ background: '#3b82f6' }}></span>
          <span>Classification Cyclone</span>
        </div>
      </div>
    </div>
  );
}

export default WeekComparisonViolinChart;
